// Spalten der Aktivitäten-Listen je Art (A-03 bis A-05), rein und ohne React (mit node --test prüfbar, deshalb nur
// Typ-Importe). Die Listen der Anrufe folgen den Spalten von Close (Lead, Kontakt, Datum, Richtung, Ergebnis, Dauer,
// Mitarbeiter, Notiz, Titel); Custom Activities zeigen die Felder des Typs als Spalten (`feld:<cf_id>`).
import type { AktivitaetenArt } from "../api/aktivitaeten";
import type { ListenSortierung, ListenSpalte } from "../components/smartview/listenVertrag";

export const FELD_VORSATZ = "feld:";
/** Höchstens so viele Felder eines Custom-Activity-Typs stehen von Anfang an als Spalten (die Tabelle bliebe sonst viel zu breit). */
export const MAX_STANDARD_FELDER = 5;

const LEAD: ListenSpalte = { key: "lead", label: "Lead", gruppe: "Zuordnung", sortierbar: true, fest: true };
const KONTAKT: ListenSpalte = { key: "contact", label: "Kontakt", gruppe: "Zuordnung" };
const DATUM = (label = "Datum"): ListenSpalte => ({ key: "activity_at", label, gruppe: "Zeit", sortierbar: true });
const GEAENDERT: ListenSpalte = { key: "date_updated", label: "Zuletzt geändert", gruppe: "Zeit", sortierbar: true };
const NUTZER: ListenSpalte = { key: "user", label: "Mitarbeiter", gruppe: "Zuordnung", sortierbar: true };
const RICHTUNG: ListenSpalte = { key: "richtung", label: "Richtung", gruppe: "Inhalt" };
const STATUS: ListenSpalte = { key: "status", label: "Status", gruppe: "Inhalt" };

export interface SpaltenSatz {
  alle: ListenSpalte[];
  standard: string[];
}

export interface FeldKopf {
  id: string;
  name: string;
}

export function spaltenFuer(art: AktivitaetenArt, felder: readonly FeldKopf[] = []): SpaltenSatz {
  switch (art) {
    case "anrufe":
      return {
        alle: [
          LEAD,
          KONTAKT,
          DATUM(),
          RICHTUNG,
          { key: "ergebnis", label: "Ergebnis", gruppe: "Inhalt" },
          { key: "dauer", label: "Dauer", gruppe: "Inhalt" },
          { key: "aufnahme", label: "Aufnahme", gruppe: "Inhalt" },
          NUTZER,
          { key: "text", label: "Notiz", gruppe: "Inhalt" },
          { key: "titel", label: "Titel", gruppe: "Inhalt" },
          { key: "telefon", label: "Telefonnummer", gruppe: "Inhalt" },
          GEAENDERT,
        ],
        standard: ["lead", "contact", "activity_at", "richtung", "ergebnis", "dauer", "aufnahme", "user", "text"],
      };
    case "emails":
      return {
        alle: [
          LEAD,
          KONTAKT,
          DATUM(),
          RICHTUNG,
          { key: "titel", label: "Betreff", gruppe: "Inhalt" },
          { key: "gegenueber", label: "Von / An", gruppe: "Inhalt" },
          { key: "text", label: "Text", gruppe: "Inhalt" },
          { key: "anhaenge", label: "Anhänge", gruppe: "Inhalt" },
          STATUS,
          NUTZER,
          GEAENDERT,
        ],
        standard: ["lead", "activity_at", "richtung", "titel", "gegenueber", "user", "anhaenge"],
      };
    case "sms":
    case "whatsapp":
      return {
        alle: [
          LEAD,
          KONTAKT,
          DATUM(),
          RICHTUNG,
          { key: "text", label: "Nachricht", gruppe: "Inhalt" },
          { key: "telefon", label: "Telefonnummer", gruppe: "Inhalt" },
          STATUS,
          NUTZER,
          GEAENDERT,
        ],
        standard: ["lead", "contact", "activity_at", "richtung", "text", "user"],
      };
    case "meetings":
      return {
        alle: [
          LEAD,
          KONTAKT,
          DATUM("Beginn"),
          { key: "titel", label: "Titel", gruppe: "Inhalt" },
          STATUS,
          { key: "ort", label: "Ort", gruppe: "Inhalt" },
          { key: "teilnehmer", label: "Teilnehmer", gruppe: "Inhalt" },
          { key: "text", label: "Notiz", gruppe: "Inhalt" },
          NUTZER,
          GEAENDERT,
        ],
        standard: ["lead", "contact", "activity_at", "titel", "status", "user", "text"],
      };
    case "notizen":
      return {
        alle: [
          LEAD,
          KONTAKT,
          DATUM(),
          { key: "titel", label: "Titel", gruppe: "Inhalt" },
          { key: "text", label: "Notiz", gruppe: "Inhalt" },
          NUTZER,
          GEAENDERT,
        ],
        standard: ["lead", "contact", "activity_at", "titel", "text", "user"],
      };
    case "custom": {
      const feldSpalten: ListenSpalte[] = felder.map((f) => ({ key: `${FELD_VORSATZ}${f.id}`, label: f.name, gruppe: "Felder" }));
      return {
        alle: [LEAD, KONTAKT, DATUM(), NUTZER, STATUS, ...feldSpalten, GEAENDERT],
        standard: ["lead", "activity_at", "user", ...feldSpalten.slice(0, MAX_STANDARD_FELDER).map((s) => s.key)],
      };
    }
  }
}

/** Spaltenschlüssel -> `sort` der Route (`activity_at`, `date_updated`, `lead`, `user`). */
export const AKTIVITAET_SORT_FELD: Readonly<Record<string, string>> = {
  activity_at: "activity_at",
  date_updated: "date_updated",
  lead: "lead",
  user: "user",
};

export const AKTIVITAET_STANDARD_SORT: ListenSortierung = { key: "activity_at", ab: true };

/** Spaltenklasse für die Breite in `aktivitaeten.css`. */
export function spaltenKlasse(key: string): string {
  if (key.startsWith(FELD_VORSATZ)) return "spalte-feld";
  switch (key) {
    case "lead":
      return "spalte-name";
    case "contact":
      return "spalte-kontakt";
    case "activity_at":
    case "date_updated":
      return "spalte-datum";
    case "richtung":
      return "spalte-richtung";
    case "ergebnis":
      return "spalte-ergebnis";
    case "dauer":
      return "spalte-dauer";
    case "user":
      return "spalte-nutzer";
    case "text":
      return "spalte-notiz";
    case "titel":
      return "spalte-titel";
    case "gegenueber":
      return "spalte-gegenueber";
    case "telefon":
      return "spalte-telefon";
    case "aufnahme":
      return "spalte-aufnahme";
    default:
      return "spalte-klein";
  }
}
